import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  FilterFn,
} from '@tanstack/react-table';
import ListToolbar, { ToolbarSearch } from './shared/ListToolbar';
import { TONE_BADGE } from '../utils/statusColors';
import {
  CELULA, CascaDaLista, TabelaDaLista, CabecalhoDaLista, ColunaDaLista, LinhaDaLista, CelulaStatus, CelulaAcoes, CartaoDoTopo,
} from './shared/lista/PecasDaLista';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

interface ProductsTableProps {
  /**
   * O termo da busca global (barra do topo) ou do "ver DDRs" do cliente (15/10/2026): a tela abre já filtrada por ele.
   * Vinha para o `search` do App, que esta tela não lê (a busca dela é própria). Mesmo padrão de Faturas e Despesas.
   */
  initialSearch?: string;
  onSearchConsumed?: () => void;
  products: any[];
  contracts: any[];
  activeProducts: number;
  canEdit: boolean;
  loading: boolean;
  onEdit: (product: any) => void;
  onDelete: (product: any) => void;
  onDuplicate: (product: any) => void;
  Icon: React.FC<{ name: string; className?: string }>;
}

const columnHelper = createColumnHelper<any>();


/**
 * D4 (12/10/2026, bloco D da revisão): três grupos de botões com "Todos" marcado três vezes, cada um com a sua cor. Viram
 * três segmentados ROTULADOS; a opção escolhida marca por peso e fundo, sem matiz.
 */
function Segmentado({ rotulo, opcoes, valor, onChange }: { rotulo: string; opcoes: string[]; valor: string; onChange: (v: string) => void }) {
  return (
    <div className='flex items-center gap-2' role='group' aria-label={rotulo}>
      <span className='text-xs text-slate-400'>{rotulo}</span>
      <div className='inline-flex rounded-lg border border-slate-700 overflow-hidden'>
        {opcoes.map((o) => (
          <button
            key={o}
            type='button'
            aria-pressed={valor === o}
            onClick={() => onChange(o)}
            className={`px-3 py-2 text-sm border-l border-slate-700 first:border-l-0 transition-colors ${valor === o ? 'bg-slate-700 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700/60'}`}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

const ProductsTable: React.FC<ProductsTableProps> = ({
  products,
  contracts,
  activeProducts,
  canEdit,
  loading,
  onEdit,
  onDelete,
  onDuplicate,
  Icon,
  initialSearch,
  onSearchConsumed,
}) => {
  // Abre ordenado por CÓDIGO crescente. O código é o que identifica o produto no
  // contrato, na fatura e na proposta — procurar por ele é o gesto normal na tela, e
  // por nome os membros de uma mesma família ficavam espalhados pela lista.
  const [sorting, setSorting] = useState<SortingState>([{ id: 'code', desc: false }]);
  const [globalFilter, setGlobalFilter] = useState(initialSearch || '');
  useEffect(() => {
    if (initialSearch) { setGlobalFilter(initialSearch); onSearchConsumed?.(); }
  }, [initialSearch, onSearchConsumed]);
  const [statusFilter, setStatusFilter] = useState('Todos');
  const [typeFilter, setTypeFilter] = useState('Todos');
  const [categoryFilter, setCategoryFilter] = useState('Todos');

  const getProductUsage = useCallback((productId: number) => {
    let count = 0;
    contracts.filter((c: any) => c.status === 'Ativo').forEach((c: any) => {
      (c.items || []).forEach((i: any) => { if (i.productId === productId) count++; });
    });
    return count;
  }, [contracts]);

  const globalFilterFn: FilterFn<any> = useCallback((row: any, _columnId: string, filterValue: any) => {
    const s = (filterValue as string).toLowerCase().trim();
    if (!s) return true;
    const p = row.original;
    return (p.name || '').toLowerCase().includes(s) ||
      (p.code || '').toLowerCase().includes(s) ||
      (p.description || '').toLowerCase().includes(s);
  }, []);

  const columns = useMemo(() => [
    columnHelper.accessor('code', { id: 'code' }),
    columnHelper.accessor('name', { id: 'name' }),
    columnHelper.accessor('category', { id: 'category' }),
    columnHelper.accessor('type', { id: 'type' }),
    columnHelper.accessor('value', {
      id: 'value',
      sortingFn: (a, b) => (parseFloat(String(a.original.value)) || 0) - (parseFloat(String(b.original.value)) || 0),
    }),
    columnHelper.accessor((row) => row.status ? 'Ativo' : 'Inativo', { id: 'status' }),
  ], []);

  const table = useReactTable({
    data: products,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const filteredData = table.getFilteredRowModel().rows.map(r => r.original);

  // Filtros manuais de status, tipo e categoria
  const filteredByFilters = useMemo(() => {
    let data = filteredData;
    if (statusFilter !== 'Todos') {
      data = data.filter((p: any) => statusFilter === 'Ativo' ? p.status : !p.status);
    }
    if (typeFilter !== 'Todos') {
      data = data.filter((p: any) => typeFilter === 'MRR' ? p.type === 'MRR' : p.type !== 'MRR');
    }
    if (categoryFilter !== 'Todos') {
      data = data.filter((p: any) => p.category === categoryFilter);
    }
    return data;
  }, [filteredData, statusFilter, typeFilter, categoryFilter]);

  // Sorting manual
  const sortedData = useMemo(() => {
    const col = sorting[0]?.id || 'name';
    const desc = sorting[0]?.desc ?? false;
    return [...filteredByFilters].sort((a: any, b: any) => {
      let cmp = 0;
      if (col === 'value') {
        cmp = (parseFloat(String(a.value)) || 0) - (parseFloat(String(b.value)) || 0);
      } else if (col === 'status') {
        cmp = (a.status ? 'Ativo' : 'Inativo').localeCompare(b.status ? 'Ativo' : 'Inativo');
      } else {
        // `numeric` porque há código com número sem zero à esquerda: alfabético puro põe
        // CNG-0800-10CH antes de CNG-0800-2CH. Vale também para o nome, onde "Canais de
        // Voz - 2 simultâneos" vinha depois de "- 10 simultâneos".
        cmp = String(a[col] || '').localeCompare(String(b[col] || ''), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        });
      }
      return desc ? -cmp : cmp;
    });
  }, [filteredByFilters, sorting]);

  const toggleSorting = useCallback((colId: string) => {
    setSorting(prev => {
      if (prev[0]?.id === colId) {
        return [{ id: colId, desc: !prev[0].desc }];
      }
      return [{ id: colId, desc: false }];
    });
  }, []);

  const getSortDir = (colId: string): 'asc' | 'desc' | false => {
    if (sorting[0]?.id !== colId) return false;
    return sorting[0].desc ? 'desc' : 'asc';
  };

  // Stats
  const productsCloud = products.filter((p: any) => p.category === 'Cloud' && p.status).length;
  const productsTelefonia = products.filter((p: any) => p.category === 'Telefonia' && p.status).length;
  const productsServicos = products.filter((p: any) => p.category === 'Serviços' && p.status).length;

  return (
    <div className='space-y-4'>
      {/* Os cartões do topo (padrão das listas, 23/10/2026): o CartaoDoTopo, com o escolhido pela barra primary, não o anel
          laranja. Clicar no escolhido volta a "Todos", como antes. */}
      <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
        <CartaoDoTopo rotulo='Total Produtos' valor={products.length} sub={`${activeProducts} ativos`} ativo={categoryFilter === 'Todos'} onClick={() => setCategoryFilter('Todos')} />
        <CartaoDoTopo rotulo='Cloud' valor={productsCloud} sub='Licenças IPCOM' ativo={categoryFilter === 'Cloud'} onClick={() => setCategoryFilter(categoryFilter === 'Cloud' ? 'Todos' : 'Cloud')} />
        <CartaoDoTopo rotulo='Telefonia' valor={productsTelefonia} sub='STFC e DDR' ativo={categoryFilter === 'Telefonia'} onClick={() => setCategoryFilter(categoryFilter === 'Telefonia' ? 'Todos' : 'Telefonia')} />
        <CartaoDoTopo rotulo='Serviços' valor={productsServicos} sub='Instalação e suporte' ativo={categoryFilter === 'Serviços'} onClick={() => setCategoryFilter(categoryFilter === 'Serviços' ? 'Todos' : 'Serviços')} />
      </div>

      <ListToolbar
        search={<ToolbarSearch value={globalFilter} onChange={setGlobalFilter} placeholder='Buscar por código, nome, descrição...' />}
        filters={<>
          <Segmentado rotulo='Categoria' opcoes={['Todos', 'Cloud', 'Telefonia', 'Serviços']} valor={categoryFilter} onChange={setCategoryFilter} />
          <Segmentado rotulo='Tipo' opcoes={['Todos', 'MRR', 'P&S']} valor={typeFilter} onChange={setTypeFilter} />
          <Segmentado rotulo='Status' opcoes={['Todos', 'Ativo', 'Inativo']} valor={statusFilter} onChange={setStatusFilter} />
        </>}
        actions={canEdit && <button onClick={() => onEdit(null as any)} className='flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-strong font-medium whitespace-nowrap'><Icon name='plus' className='w-5 h-5' />Novo Produto</button>}
      />

      {/* A tabela nas peças da lista (23/10/2026): casca, cabeçalho fixo e ordenável, linhas de 3 e as ações no editar + ⋯. */}
      {loading ? <div className='text-center py-12 text-slate-500'>Carregando...</div> : (
        <CascaDaLista>
          <TabelaDaLista>
            <CabecalhoDaLista>
              <ColunaDaLista rotulo='Código' largura='w-44' ordem={getSortDir('code')} onOrdenar={() => toggleSorting('code')} />
              <ColunaDaLista rotulo='Produto' ordem={getSortDir('name')} onOrdenar={() => toggleSorting('name')} />
              <ColunaDaLista rotulo='Categoria' largura='w-28' alinhamento='centro' ordem={getSortDir('category')} onOrdenar={() => toggleSorting('category')} />
              <ColunaDaLista rotulo='Tipo' largura='w-20' alinhamento='centro' ordem={getSortDir('type')} onOrdenar={() => toggleSorting('type')} />
              <ColunaDaLista rotulo='Valor' largura='w-32' alinhamento='direita' ordem={getSortDir('value')} onOrdenar={() => toggleSorting('value')} />
              <ColunaDaLista rotulo='Fiscal' largura='w-24' alinhamento='centro' />
              <ColunaDaLista rotulo='Em Uso' largura='w-32' alinhamento='centro' />
              <ColunaDaLista rotulo='Status' largura='w-24' alinhamento='centro' ordem={getSortDir('status')} onOrdenar={() => toggleSorting('status')} />
              {/* Sem permissão de escrita a coluna inteira sai: cabeçalho "Ações" sobre células vazias só faz a pessoa procurar
                  o botão que não existe. */}
              {canEdit && <ColunaDaLista rotulo='Ações' largura='w-28' alinhamento='direita' />}
            </CabecalhoDaLista>
            <tbody className='divide-y divide-central-borda'>
              {sortedData.length === 0 ? <tr><td colSpan={9} className='px-4 py-12 text-center text-slate-500'>Nenhum produto encontrado</td></tr> : sortedData.map((p: any) => {
                const usage = getProductUsage(p.id);
                return (
                <LinhaDaLista key={p.id}>
                  {/* O código é o que identifica o produto no contrato, na fatura e na proposta: texto mono, sem pílula. */}
                  <td className={`${CELULA} whitespace-nowrap`}><span className='font-mono text-sm text-central-texto'>{p.code}</span></td>
                  <td className={CELULA}>
                    <p className='font-medium text-central-texto truncate'>{p.name}</p>
                    <p className='text-xs text-slate-500 truncate'>{p.description}</p>
                  </td>
                  <td className={`${CELULA} text-center`}>
                    {/* D5: Categoria é texto; os selos da linha são só Tipo e Em uso. */}
                    <span className='text-sm text-slate-300'>{p.category || '—'}</span>
                  </td>
                  <td className={`${CELULA} text-center`}>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TONE_BADGE.neutral}`}>{p.type}</span>
                  </td>
                  <td className={`${CELULA} text-right whitespace-nowrap`}>
                    <span className='text-sm font-semibold text-central-texto tabular-nums'>R$ {fmt(p.value)}</span>
                    {Array.isArray(p.priceTiers) && p.priceTiers.length > 1 && (
                      <p className='text-[11px] font-normal text-slate-500' title='Preço por faixa de volume'>
                        por faixa · até R$ {fmt(p.priceTiers[p.priceTiers.length - 1].unitPrice)}
                      </p>
                    )}
                  </td>
                  <td className={`${CELULA} text-center`}>
                    <span className='text-sm text-slate-300'>{p.fiscalType === 'nfcom' ? 'NFCom' : p.fiscalType === 'isento' ? 'Isento' : 'NFS-e'}</span>
                  </td>
                  <td className={`${CELULA} text-center`}>
                    {usage > 0 ? (
                      <span className={`${TONE_BADGE.neutral} px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap`}>{usage} assin.</span>
                    ) : (
                      <span className='text-sm text-slate-500'>Nenhum</span>
                    )}
                  </td>
                  {/* Ativo é o normal: neutro, sem selo. Inativo, no tom de Clientes. */}
                  {p.status ? <td className={CELULA} /> : <CelulaStatus texto='Inativo' tom='danger' />}
                  {/* D2 (12/10/2026): Duplicar e Excluir no ⋯; Excluir separado por traço, com confirm. O editar é o da peça
                      (o hover verde saiu). */}
                  {canEdit && (
                    <CelulaAcoes
                      rotuloDoMenu={`Mais ações do produto ${p.code}`}
                      onEditar={() => onEdit(p)}
                      Icon={Icon}
                      itens={[
                        { label: 'Duplicar', onClick: () => onDuplicate(p) },
                        { label: 'Excluir', onClick: () => onDelete(p), danger: true, separadoAntes: true },
                      ]}
                    />
                  )}
                </LinhaDaLista>
              ); })}
            </tbody>
          </TabelaDaLista>
        </CascaDaLista>
      )}
    </div>
  );
};

export default ProductsTable;
